import { useState, useEffect, useCallback } from 'react';
import { generateQuestion, generateEssayPrompt, gradeEssay } from '../api';
import { SUBJECT_COLORS, SUBJECT_GRADIENTS, SUBJECT_EMOJIS } from '../data';
import supabase from '../lib/supabase';
import { QuestionText, GeometryFigure } from './QuizQuestion';

function FlagButton({ question }) {
  const [flagged, setFlagged] = useState(false);
  async function handleFlag() {
    setFlagged(true);
    const { data: { session } } = await supabase.auth.getSession();
    await supabase.from('flagged_questions').insert({
      user_id: session?.user?.id ?? null,
      subject: question.subject ?? null,
      topic: question.concept ?? null,
      question: question.question,
      options: question.options,
      answer: question.answer,
      explanation: question.explanation ?? null,
    });
  }
  return (
    <button
      className="flag-btn"
      onClick={handleFlag}
      disabled={flagged}
      title="Report an issue with this question"
    >
      {flagged ? '⚑ Reported — thanks!' : '⚑ Report incorrect answer'}
    </button>
  );
}

const EXAM_SUBJECTS = ['Math', 'Science', 'Social Studies', 'Language Arts'];

async function saveExamSession(userId, state) {
  if (!userId) return;
  await supabase.from('exam_sessions').upsert({
    user_id: userId,
    phase: state.phase,
    subject_idx: state.subjectIdx,
    question_idx: state.questionIdx,
    current_q: state.currentQ,
    scores: state.scores,
    used_questions: [...state.usedQuestions],
    essay_data: state.essayData,
    essay_text: state.essayText,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'user_id' });
}

async function clearExamSession(userId) {
  if (!userId) return;
  await supabase.from('exam_sessions').delete().eq('user_id', userId);
  localStorage.removeItem('gedready-exam-state');
}

export default function MockExam({ user, isPremium, onUpgrade, onComplete, t, lang, tSubject }) {
  // Fallback so MockExam works even if t/tSubject not passed
  if (!t) t = (k) => k;
  if (!tSubject) tSubject = (k) => k;
  const QS = isPremium ? 10 : 1;
  const SUBJECT_COUNT = isPremium ? 4 : 2;

  const [phase, setPhase] = useState('intro');
  const [subjectIdx, setSubjectIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [currentQ, setCurrentQ] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);

  const [scores, setScores] = useState(() =>
    Object.fromEntries(EXAM_SUBJECTS.map(s => [s, { correct: 0, total: 0 }]))
  );

  const [usedQuestions, setUsedQuestions] = useState(() => new Set());
  const [essayData, setEssayData] = useState(null);
  const [essayText, setEssayText] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [grading, setGrading] = useState(false);

  // Load saved session from Supabase when user is available
  useEffect(() => {
    if (!user) return;
    supabase.from('exam_sessions').select('*').eq('user_id', user.id).single()
      .then(({ data }) => {
        if (data && data.phase && data.phase !== 'intro') {
          setPhase(data.phase);
          setSubjectIdx(data.subject_idx || 0);
          setQuestionIdx(data.question_idx || 0);
          setCurrentQ(data.current_q || null);
          setScores(data.scores || Object.fromEntries(EXAM_SUBJECTS.map(s => [s, { correct: 0, total: 0 }])));
          setUsedQuestions(new Set(data.used_questions || []));
          setEssayData(data.essay_data || null);
          setEssayText(data.essay_text || '');
          setWordCount((data.essay_text || '').trim().split(/\s+/).filter(Boolean).length);
        }
        setSessionLoaded(true);
      });
  }, [user]);

  // Save to Supabase whenever state changes (after initial load)
  useEffect(() => {
    if (!sessionLoaded || !user) return;
    if (phase === 'intro') { clearExamSession(user.id); return; }
    saveExamSession(user.id, { phase, subjectIdx, questionIdx, currentQ, scores, usedQuestions, essayData, essayText });
  }, [phase, subjectIdx, questionIdx, currentQ, scores, usedQuestions, essayData, essayText, sessionLoaded, user]);

  const subject = EXAM_SUBJECTS[subjectIdx];
  const totalQ = QS * SUBJECT_COUNT;
  const answeredSoFar = EXAM_SUBJECTS.slice(0, subjectIdx).reduce(
    (s, sub) => s + (scores[sub]?.total || 0), 0
  ) + questionIdx;

  const loadQuestion = useCallback(async () => {
    setPhase('loading');
    setLoadError(null);
    setSelected(null);
    setCurrentQ(null);
    try {
      // Pick a random topic from current subject
      const { TOPICS } = await import('../data');
      const topics = TOPICS[subject];
      const topic = topics[Math.floor(Math.random() * topics.length)];
      const recentTexts = [...usedQuestions].slice(-5);
      const q = await generateQuestion(subject, topic, recentTexts);
      setUsedQuestions(prev => new Set(prev).add(q.question));
      setCurrentQ(q);
      setPhase('question');
    } catch (e) {
      setLoadError(e.message || 'Failed to load question.');
      setPhase('loading');
    }
  }, [subject, usedQuestions]);

  function startExam() {
    loadQuestion();
  }

  function handleSelect(option) {
    if (selected !== null) return;
    setSelected(option);
    const isCorrect = option === currentQ.answer;
    setScores(prev => ({
      ...prev,
      [subject]: {
        correct: prev[subject].correct + (isCorrect ? 1 : 0),
        total: prev[subject].total + 1,
      },
    }));
  }

  function handleNext() {
    const nextQIdx = questionIdx + 1;

    if (nextQIdx < QS) {
      // More questions in this subject
      setQuestionIdx(nextQIdx);
      loadQuestion();
    } else {
      // Subject done
      const nextSubjectIdx = subjectIdx + 1;
      if (nextSubjectIdx < SUBJECT_COUNT) {
        setSubjectIdx(nextSubjectIdx);
        setQuestionIdx(0);
        setPhase('between');
      } else if (!isPremium) {
        setPhase('upgrade-gate');
      } else {
        // Load essay
        loadEssay();
      }
    }
  }

  async function loadEssay() {
    setPhase('essay-loading');
    try {
      const data = await generateEssayPrompt();
      setEssayData(data);
      setPhase('essay');
    } catch (e) {
      setLoadError(e.message || 'Failed to load essay prompt.');
    }
  }

  function handleEssayChange(e) {
    const text = e.target.value;
    setEssayText(text);
    setWordCount(text.trim() === '' ? 0 : text.trim().split(/\s+/).length);
  }

  async function handleSubmitEssay() {
    setGrading(true);
    try {
      const grade = await gradeEssay(essayData.prompt, essayText);
      await clearExamSession(user?.id);
      onComplete({ scores, essay: grade, isPremium });
    } catch (e) {
      setGrading(false);
      setLoadError(e.message || 'Failed to grade essay. Please try again.');
    }
  }

  const color = SUBJECT_COLORS[subject] || '#6366f1';
  const gradient = SUBJECT_GRADIENTS[subject] || 'linear-gradient(135deg,#6366f1,#8b5cf6)';

  // ── INTRO ──
  if (phase === 'intro') {
    return (
      <div className="exam-intro">
        <div className="exam-intro-icon">📝</div>
        <h1 className="exam-intro-title">GED Mock Exam</h1>
        {isPremium ? (
          <>
            <p className="exam-intro-desc">
              Simulate real GED testing conditions. <strong>40 questions</strong> across all 4 subjects
              plus a <strong>graded essay</strong> — no hints allowed.
            </p>
            <div className="exam-subject-preview">
              {EXAM_SUBJECTS.map(s => (
                <div key={s} className="exam-subject-chip" style={{ background: SUBJECT_GRADIENTS[s] }}>
                  {SUBJECT_EMOJIS[s]} {tSubject(s)} <span>10 Qs</span>
                </div>
              ))}
            </div>
            <p className="exam-real-note">
              The real GED has ~167 questions across 4 subjects (Math 46, Science 40, Social Studies 35, Language Arts 46) and takes ~7 hours. This is a condensed simulation. Passing score: 150/200 per subject.
            </p>
          </>
        ) : (
          <>
            <p className="exam-intro-desc">
              Preview the mock exam with <strong>2 questions</strong> across Math &amp; Science.
              Upgrade for the full 40-question exam + essay grading.
            </p>
            <div className="exam-upgrade-note" onClick={onUpgrade}>
              ⭐ Upgrade for the full exam →
            </div>
          </>
        )}
        <button className="primary-btn exam-start-btn" style={{ background: gradient }} onClick={startExam}>
          Start Exam
        </button>
      </div>
    );
  }

  // ── UPGRADE GATE ──
  if (phase === 'upgrade-gate') {
    return (
      <div className="exam-intro">
        <div className="exam-intro-icon">⭐</div>
        <h2 className="exam-intro-title">Preview Complete!</h2>
        <p className="exam-intro-desc">
          You answered <strong>2 preview questions</strong>. Upgrade to unlock the full
          40-question mock exam with AI essay grading.
        </p>
        <button className="primary-btn exam-start-btn" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }} onClick={onUpgrade}>
          Unlock Full Exam — Upgrade
        </button>
      </div>
    );
  }

  // ── BETWEEN SUBJECTS ──
  if (phase === 'between') {
    const prevSubject = EXAM_SUBJECTS[subjectIdx - 1];
    const prevScore = scores[prevSubject];
    const pct = prevScore.total > 0 ? Math.round((prevScore.correct / prevScore.total) * 100) : 0;
    return (
      <div className="exam-between">
        <div className="exam-between-icon">{SUBJECT_EMOJIS[prevSubject]}</div>
        <h2>{tSubject(prevSubject)} Complete</h2>
        <p className="exam-between-score">{prevScore.correct}/{prevScore.total} correct — {pct}%</p>
        <p className="exam-between-next">Up next: <strong>{tSubject(EXAM_SUBJECTS[subjectIdx])}</strong></p>
        <button
          className="primary-btn"
          style={{ background: SUBJECT_GRADIENTS[EXAM_SUBJECTS[subjectIdx]] }}
          onClick={loadQuestion}
        >
          Continue →
        </button>
      </div>
    );
  }

  // ── LOADING ──
  if (phase === 'loading' || (phase === 'essay-loading' && !essayData)) {
    return (
      <div className="exam-loading">
        {loadError ? (
          <>
            <p className="exam-error">{loadError}</p>
            <button className="primary-btn" style={{ background: gradient }} onClick={loadQuestion}>Retry</button>
          </>
        ) : (
          <>
            <div className="exam-spinner" style={{ borderTopColor: color }} />
            <p>{phase === 'essay-loading' ? 'Generating essay prompt…' : `Loading ${tSubject(subject)} question…`}</p>
          </>
        )}
      </div>
    );
  }

  // ── GRADING ──
  if (grading) {
    return (
      <div className="exam-loading">
        <div className="exam-spinner" style={{ borderTopColor: '#6366f1' }} />
        <p>AI is grading your essay…</p>
        <p className="exam-grading-sub">This takes about 10 seconds</p>
      </div>
    );
  }

  // ── ESSAY ──
  if (phase === 'essay' && essayData) {
    return (
      <div className="essay-screen">
        <div className="essay-header">
          <span className="essay-label">Extended Response — Language Arts</span>
          <span className="exam-q-counter">Final Section</span>
        </div>
        <div className="essay-passage">
          <p className="essay-passage-label">Read the following passage:</p>
          <p className="essay-passage-text">{essayData.passage}</p>
        </div>
        <div className="essay-prompt-box">
          <p className="essay-prompt-text">{essayData.prompt}</p>
        </div>
        <div className="essay-write-area">
          <textarea
            className="essay-textarea"
            placeholder="Write your response here…"
            value={essayText}
            onChange={handleEssayChange}
            rows={12}
          />
          <div className="essay-footer">
            <span className={`essay-wordcount ${wordCount >= 50 ? 'essay-wordcount--ok' : ''}`}>
              {wordCount} words {wordCount < 50 ? `(min 50)` : '✓'}
            </span>
            <button
              className="primary-btn"
              style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}
              disabled={wordCount < 50}
              onClick={handleSubmitEssay}
            >
              Submit Essay
            </button>
          </div>
        </div>
        {loadError && <p className="exam-error" style={{ marginTop: 12 }}>{loadError}</p>}
      </div>
    );
  }

  // ── QUESTION ──
  if (phase === 'question' && currentQ) {
    return (
      <div className="exam-question-screen">
        {/* Progress bar */}
        <div className="exam-progress">
          {EXAM_SUBJECTS.slice(0, SUBJECT_COUNT).map((s, i) => (
            <div
              key={s}
              className={`exam-subject-tab ${i === subjectIdx ? 'exam-subject-tab--active' : ''} ${i < subjectIdx ? 'exam-subject-tab--done' : ''}`}
              style={i === subjectIdx ? { background: SUBJECT_GRADIENTS[s], color: '#fff' } : {}}
            >
              {SUBJECT_EMOJIS[s]}
              <span className="exam-tab-name">{tSubject(s)}</span>
              {i < subjectIdx && <span className="exam-tab-check">✓</span>}
            </div>
          ))}
        </div>

        <div className="exam-q-header">
          <span className="exam-q-subject" style={{ color }}>{tSubject(subject)}</span>
          <span className="exam-q-counter">Q{questionIdx + 1} of {QS} &nbsp;·&nbsp; {answeredSoFar + 1} of {totalQ} total</span>
        </div>

        <div className="exam-q-panel">
          <div className="q-meta">
            <span className="q-concept">{currentQ.concept}</span>
            <span className={`q-diff q-diff--${currentQ.difficulty}`}>{currentQ.difficulty}</span>
          </div>
          <QuestionText text={currentQ.question} />
          <GeometryFigure figure={currentQ.figure} />
          <div className="q-options">
            {currentQ.options.map((opt, i) => {
              let style = {};
              if (selected !== null) {
                if (opt === currentQ.answer) style = { background: '#059669', color: '#fff', borderColor: '#059669' };
                else if (opt === selected) style = { background: '#dc2626', color: '#fff', borderColor: '#dc2626' };
                else style = { opacity: 0.45 };
              }
              return (
                <button
                  key={i}
                  className="q-option"
                  style={style}
                  onClick={() => handleSelect(opt)}
                >
                  <span className="q-option-letter">{['A','B','C','D'][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>

          {selected !== null && (
            <div className="exam-next-row">
              <span className={`exam-verdict ${selected === currentQ.answer ? 'exam-verdict--correct' : 'exam-verdict--wrong'}`}>
                {selected === currentQ.answer ? '✓ Correct' : `✗ Correct answer: ${currentQ.answer}`}
              </span>
              <button className="primary-btn" style={{ background: gradient }} onClick={handleNext}>
                {questionIdx + 1 < QS ? 'Next Question →' :
                 subjectIdx + 1 < SUBJECT_COUNT ? 'Next Subject →' :
                 isPremium ? 'Continue to Essay →' : 'See Results →'}
              </button>
            </div>
          )}
          {selected !== null && (
            <FlagButton question={currentQ} />
          )}
        </div>
      </div>
    );
  }

  return null;
}
